import { PLACE_DETAILS, PLACE_SUGGESTIONS } from "@/gql/places";
import { bengaluruGps } from "@/info/bengaluru-gps";
import { logger } from "@/utils/logger";
import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AutocompleteActions, AutocompleteState, PlaceDetails, PlaceSuggestion, SuggestionItem } from "./types";
import { transformSuggestions } from "./utils";
const DEBOUNCE_TIMEOUT = 200;

// GraphQL response interfaces for place suggestions
interface PlaceSuggestionResponse {
  placeId: string;
  mainText: string;
  secondaryText: string;
  placeName: string;
}
interface AddressComponentResponse {
  longText: string;
  shortText: string;
  types: string[];
}
const isProduction = process.env.NODE_ENV === "production";

// Generate a UUID for session token (client-side)
const generateSessionToken = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
export const usePlaceAutocomplete = (onPlaceSelect?: (place: PlaceDetails) => void, isValueSelected: boolean = false, onSelectionMade?: (suggestion: PlaceSuggestion) => void, initialValue: string = ""): AutocompleteState & AutocompleteActions & {
  isReady: boolean;
} => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Apollo query for fetching place suggestions from new API
  const [fetchPlaceSuggestions, {
    loading: suggestionsLoading
  }] = useLazyQuery(PLACE_SUGGESTIONS, {
    fetchPolicy: isProduction ? "cache-first" : "network-only",
    // Use cache to reduce API costs
    errorPolicy: "all"
  });

  // Apollo query for fetching place details by ID
  const [fetchPlaceDetails] = useLazyQuery(PLACE_DETAILS, {
    fetchPolicy: isProduction ? "cache-first" : "network-only",
    errorPolicy: "all"
  });

  // Refs for managing component lifecycle and API calls
  const isMountedRef = useRef(true);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Client-side session token for Google Places API cost optimization
  const sessionTokenRef = useRef<string>(generateSessionToken());

  // Reset session token (called after place selection to start a new session)
  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = generateSessionToken();
  }, []);

  // Reset mounted ref on mount (important for dynamic components)
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Stable suggestion items - only recalculate when necessary
  const suggestionItems: SuggestionItem[] = transformSuggestions(suggestions, isLoading, inputValue, isValueSelected);

  // Fetch suggestions using new places API
  const fetchSuggestions = useCallback(async (query: string) => {
    if (!isMountedRef.current) {
      return;
    }
    try {
      // Use Apollo query to fetch suggestions from new places API
      // Include session token for Google Places API cost optimization
      const {
        data
      } = await fetchPlaceSuggestions({
        variables: {
          params: {
            placeSuggestions: {
              input: query,
              city: "bengaluru",
              sessionToken: sessionTokenRef.current
            }
          }
        }
      });
      if (isMountedRef.current && data?.locations?.placeSuggestions) {
        // Transform result to match the expected suggestion format
        const newSuggestions = data.locations.placeSuggestions.map((place: PlaceSuggestionResponse) => ({
          placeId: place.placeId,
          mainText: place.mainText,
          secondaryText: place.secondaryText,
          placeName: place.placeName
        }));
        setSuggestions(newSuggestions);
        setIsLoading(false);
        // logger.debug(`Loaded ${newSuggestions.length} place suggestions for "${query}"`);
      } else {
        // Fallback to empty suggestions if no data
        if (isMountedRef.current) {
          setSuggestions([]);
          setIsLoading(false);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        logger.error(`Failed to fetch place suggestions: ${error}`);
        setSuggestions([]);
        setIsLoading(false);
      }
    }
  }, [fetchPlaceSuggestions]);

  // Handle input changes with debouncing
  const handleInputChange = useCallback((value: string) => {
    if (!isMountedRef.current) {
      return;
    }
    setInputValue(value);

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (value.trim() === "") {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Validate minimum character length to prevent GraphQL error
    if (value.trim().length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Set loading state immediately for better UX
    setIsLoading(true);

    // Debounce API call using Apollo query (no places library dependency for suggestions)
    debounceTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, DEBOUNCE_TIMEOUT);
  }, [fetchSuggestions]);

  // Handle place selection
  const handlePlaceSelect = useCallback(async (selectedId: string) => {
    if (!isMountedRef.current) {
      return;
    }
    const suggestion = suggestions.find(s => s.placeId === selectedId);
    if (!suggestion) {
      logger.error(`Could not find suggestion with ID: ${selectedId}`);
      return;
    }
    const predictionText = suggestion.mainText || "";

    // Update UI immediately
    setInputValue(predictionText);
    setSuggestions([]);
    // Call the selection callback immediately with the text and suggestion data
    onSelectionMade?.(suggestion);
    try {
      // First try to get details from new places API
      // Include session token for Google Places API cost optimization
      // Using the same token as autocomplete completes the session
      const {
        data: data_0
      } = await fetchPlaceDetails({
        variables: {
          params: {
            placeDetails: {
              placeId: selectedId,
              sessionToken: sessionTokenRef.current
            }
          }
        }
      });

      // Reset session token after place selection (starts a new session for next search)
      resetSessionToken();
      if (isMountedRef.current && onPlaceSelect) {
        const place_0 = data_0.locations.placeDetails;
        if (!place_0) throw new Error("Place details not found");
        const placeDetails: PlaceDetails = {
          displayName: place_0.displayName || "",
          formattedAddress: place_0.formattedAddress || "",
          id: place_0.placeId || "",
          // Use placeId from GraphQL response
          location: place_0.gps && place_0.gps.coordinates ? {
            lat: place_0.gps.coordinates[1],
            // latitude is second in GeoJSON
            lng: place_0.gps.coordinates[0] // longitude is first in GeoJSON
          } : {
            lat: bengaluruGps.latitude,
            lng: bengaluruGps.longitude
          },
          addressComponents: place_0.addressComponents?.map((component: AddressComponentResponse) => ({
            longText: component.longText || "",
            shortText: component.shortText || "",
            types: component.types || []
          })) || [],
          types: place_0.types || []
        };
        onPlaceSelect(placeDetails);
      }
    } catch (error_0) {
      // Reset session token even on error to ensure clean state
      resetSessionToken();
      if (isMountedRef.current) {
        logger.error(`Failed to fetch place details: ${error_0}`);
        setSuggestions([]);
        setIsLoading(false);
      }
    }
  }, [fetchPlaceDetails, suggestions, onPlaceSelect, onSelectionMade, resetSessionToken]);

  // Clear input
  const clearInput = useCallback(() => {
    if (!isMountedRef.current) return;
    setInputValue("");
    setSuggestions([]);
    setIsLoading(false);
  }, []);
  return {
    inputValue,
    suggestions,
    isLoading: isLoading || suggestionsLoading,
    suggestionItems,
    handleInputChange,
    handlePlaceSelect,
    clearInput,
    isReady: true // Always ready since we use Apollo query (doesn't require places library)
  };
};